import { Entity } from '../Entity';
import { Scene } from '../Scene';
import { QueryBuilder } from './QuerySystem';
/**
 * 实体构建器 - 提供流式API创建和配置实体
 */
export class EntityBuilder {
    constructor(scene, storageManager) {
        this.scene = scene;
        this.storageManager = storageManager;
        this.entity = new Entity("", scene.identifierPool.checkOut());
    }
    /**
     * 设置实体名称
     * @param name 实体名称
     * @returns 实体构建器
     */
    named(name) {
        this.entity.name = name;
        return this;
    }
    /**
     * 设置实体标签
     * @param tag 标签
     * @returns 实体构建器
     */
    tagged(tag) {
        this.entity.tag = tag;
        return this;
    }
    /**
     * 添加组件
     * @param component 组件实例
     * @returns 实体构建器
     */
    with(component) {
        this.entity.addComponent(component);
        return this;
    }
    /**
     * 添加多个组件
     * @param components 组件数组
     * @returns 实体构建器
     */
    withComponents(...components) {
        for (const component of components) {
            this.entity.addComponent(component);
        }
        return this;
    }
    /**
     * 条件性添加组件
     * @param condition 条件
     * @param component 组件实例
     * @returns 实体构建器
     */
    withIf(condition, component) {
        if (condition) {
            this.entity.addComponent(component);
        }
        return this;
    }
    /**
     * 使用工厂函数创建并添加组件
     * @param factory 组件工厂函数
     * @returns 实体构建器
     */
    withFactory(factory) {
        const component = factory();
        this.entity.addComponent(component);
        return this;
    }
    /**
     * 配置组件属性
     * @param componentType 组件类型
     * @param configurator 配置函数
     * @returns 实体构建器
     */
    configure(componentType, configurator) {
        const component = this.entity.getComponent(componentType);
        if (component) {
            configurator(component);
        }
        return this;
    }
    /**
     * 设置实体为启用状态
     * @param enabled 是否启用
     * @returns 实体构建器
     */
    enabled(enabled = true) {
        this.entity.enabled = enabled;
        return this;
    }
    /**
     * 设置实体为活跃状态
     * @param active 是否活跃
     * @returns 实体构建器
     */
    active(active = true) {
        this.entity.active = active;
        return this;
    }
    /**
     * 添加子实体
     * @param childBuilder 子实体构建器
     * @returns 实体构建器
     */
    withChild(childBuilder) {
        const child = childBuilder.build();
        this.entity.addChild(child);
        return this;
    }
    /**
     * 批量添加子实体
     * @param childBuilders 子实体构建器数组
     * @returns 实体构建器
     */
    withChildren(...childBuilders) {
        for (const childBuilder of childBuilders) {
            const child = childBuilder.build();
            this.entity.addChild(child);
        }
        return this;
    }
    /**
     * 使用工厂函数创建子实体
     * @param childFactory 子实体工厂函数
     * @returns 实体构建器
     */
    withChildFactory(childFactory) {
        const childBuilder = childFactory(this.entity);
        const child = childBuilder.build();
        this.entity.addChild(child);
        return this;
    }
    /**
     * 条件性添加子实体
     * @param condition 条件
     * @param childBuilder 子实体构建器
     * @returns 实体构建器
     */
    withChildIf(condition, childBuilder) {
        if (condition) {
            const child = childBuilder.build();
            this.entity.addChild(child);
        }
        return this;
    }
    /**
     * 构建并返回实体
     * @returns 构建的实体
     */
    build() {
        return this.entity;
    }
    /**
     * 构建实体并添加到场景
     * @returns 构建的实体
     */
    spawn() {
        this.scene.addEntity(this.entity);
        return this.entity;
    }
    /**
     * 克隆当前构建器
     * @returns 新的实体构建器
     */
    clone() {
        const newBuilder = new EntityBuilder(this.scene, this.storageManager);
        // 这里需要深度克隆实体，简化实现
        newBuilder.entity = this.entity; // 实际应该是深度克隆
        return newBuilder;
    }
}
/**
 * 场景构建器 - 提供流式API创建和配置场景
 */
export class SceneBuilder {
    constructor() {
        this.scene = new Scene();
    }
    /**
     * 设置场景名称
     * @param name 场景名称
     * @returns 场景构建器
     */
    named(name) {
        this.scene.name = name;
        return this;
    }
    /**
     * 添加实体
     * @param entity 实体
     * @returns 场景构建器
     */
    withEntity(entity) {
        this.scene.addEntity(entity);
        return this;
    }
    /**
     * 使用实体构建器添加实体
     * @param builderFn 实体构建器函数
     * @returns 场景构建器
     */
    withEntityBuilder(builderFn) {
        const builder = new EntityBuilder(this.scene, this.scene.componentStorageManager);
        const configuredBuilder = builderFn(builder);
        const entity = configuredBuilder.build();
        this.scene.addEntity(entity);
        return this;
    }
    /**
     * 批量添加实体
     * @param entities 实体数组
     * @returns 场景构建器
     */
    withEntities(...entities) {
        for (const entity of entities) {
            this.scene.addEntity(entity);
        }
        return this;
    }
    /**
     * 添加系统
     * @param system 系统实例
     * @returns 场景构建器
     */
    withSystem(system) {
        this.scene.addSystem(system);
        return this;
    }
    /**
     * 批量添加系统
     * @param systems 系统数组
     * @returns 场景构建器
     */
    withSystems(...systems) {
        for (const system of systems) {
            this.scene.addSystem(system);
        }
        return this;
    }
    /**
     * 构建并返回场景
     * @returns 构建的场景
     */
    build() {
        return this.scene;
    }
}
/**
 * 组件构建器 - 提供流式API创建组件
 */
export class ComponentBuilder {
    constructor(componentClass, ...args) {
        this.component = new componentClass(...args);
    }
    /**
     * 设置组件属性
     * @param property 属性名
     * @param value 属性值
     * @returns 组件构建器
     */
    set(property, value) {
        this.component[property] = value;
        return this;
    }
    /**
     * 使用配置函数设置组件
     * @param configurator 配置函数
     * @returns 组件构建器
     */
    configure(configurator) {
        configurator(this.component);
        return this;
    }
    /**
     * 条件性设置属性
     * @param condition 条件
     * @param property 属性名
     * @param value 属性值
     * @returns 组件构建器
     */
    setIf(condition, property, value) {
        if (condition) {
            this.component[property] = value;
        }
        return this;
    }
    /**
     * 构建并返回组件
     * @returns 构建的组件
     */
    build() {
        return this.component;
    }
}
/**
 * ECS流式API主入口
 * 提供统一的流式接口
 */
export class ECSFluentAPI {
    constructor(scene, querySystem, eventSystem) {
        this.scene = scene;
        this.querySystem = querySystem;
        this.eventSystem = eventSystem;
    }
    /**
     * 创建实体构建器
     * @returns 实体构建器
     */
    createEntity() {
        return new EntityBuilder(this.scene, this.scene.componentStorageManager);
    }
    /**
     * 创建场景构建器
     * @returns 场景构建器
     */
    createScene() {
        return new SceneBuilder();
    }
    /**
     * 创建组件构建器
     * @param componentClass 组件类
     * @param args 构造参数
     * @returns 组件构建器
     */
    createComponent(componentClass, ...args) {
        return new ComponentBuilder(componentClass, ...args);
    }
    /**
     * 创建查询构建器
     * @returns 查询构建器
     */
    query() {
        return new QueryBuilder(this.querySystem);
    }
    /**
     * 查找实体（简化版）
     * @param componentTypes 组件类型
     * @returns 实体数组
     */
    find(...componentTypes) {
        return this.querySystem.queryAll(...componentTypes).entities;
    }
    /**
     * 查找第一个匹配的实体
     * @param componentTypes 组件类型
     * @returns 实体或null
     */
    findFirst(...componentTypes) {
        const result = this.querySystem.queryAll(...componentTypes);
        return result.entities.length > 0 ? result.entities[0] : null;
    }
    /**
     * 按名称查找实体
     * @param name 实体名称
     * @returns 实体或null
     */
    findByName(name) {
        return this.scene.getEntityByName(name);
    }
    /**
     * 按标签查找实体
     * @param tag 标签
     * @returns 实体数组
     */
    findByTag(tag) {
        return this.scene.getEntitiesByTag(tag);
    }
    /**
     * 触发事件
     * @param eventType 事件类型
     * @param event 事件数据
     */
    emit(eventType, event) {
        this.eventSystem.emitSync(eventType, event);
    }
    /**
     * 异步触发事件
     * @param eventType 事件类型
     * @param event 事件数据
     */
    async emitAsync(eventType, event) {
        await this.eventSystem.emit(eventType, event);
    }
    /**
     * 监听事件
     * @param eventType 事件类型
     * @param handler 事件处理器
     * @returns 监听器ID
     */
    on(eventType, handler) {
        return this.eventSystem.on(eventType, handler);
    }
    /**
     * 一次性监听事件
     * @param eventType 事件类型
     * @param handler 事件处理器
     * @returns 监听器ID
     */
    once(eventType, handler) {
        return this.eventSystem.once(eventType, handler);
    }
    /**
     * 移除事件监听器
     * @param eventType 事件类型
     * @param listenerId 监听器ID
     */
    off(eventType, listenerId) {
        this.eventSystem.off(eventType, listenerId);
    }
    /**
     * 批量操作实体
     * @param entities 实体数组
     * @returns 批量操作器
     */
    batch(entities) {
        return new EntityBatchOperator(entities);
    }
    /**
     * 获取场景统计信息
     * @returns 统计信息
     */
    getStats() {
        return {
            entityCount: this.scene.entities.count,
            systemCount: this.scene.systems.length,
            componentStats: this.scene.componentStorageManager.getAllStats(),
            queryStats: this.querySystem.getStats(),
            eventStats: this.eventSystem.getStats()
        };
    }
}
/**
 * 实体批量操作器
 * 提供对多个实体的批量操作
 */
export class EntityBatchOperator {
    constructor(entities) {
        this.entities = entities;
    }
    /**
     * 批量添加组件
     * @param component 组件实例
     * @returns 批量操作器
     */
    addComponent(component) {
        for (const entity of this.entities) {
            entity.addComponent(component);
        }
        return this;
    }
    /**
     * 批量移除组件
     * @param componentType 组件类型
     * @returns 批量操作器
     */
    removeComponent(componentType) {
        for (const entity of this.entities) {
            entity.removeComponentByType(componentType);
        }
        return this;
    }
    /**
     * 批量设置活跃状态
     * @param active 是否活跃
     * @returns 批量操作器
     */
    setActive(active) {
        for (const entity of this.entities) {
            entity.active = active;
        }
        return this;
    }
    /**
     * 批量设置标签
     * @param tag 标签
     * @returns 批量操作器
     */
    setTag(tag) {
        for (const entity of this.entities) {
            entity.tag = tag;
        }
        return this;
    }
    /**
     * 批量执行操作
     * @param operation 操作函数
     * @returns 批量操作器
     */
    forEach(operation) {
        this.entities.forEach(operation);
        return this;
    }
    /**
     * 过滤实体
     * @param predicate 过滤条件
     * @returns 新的批量操作器
     */
    filter(predicate) {
        return new EntityBatchOperator(this.entities.filter(predicate));
    }
    /**
     * 获取实体数组
     * @returns 实体数组
     */
    toArray() {
        return this.entities.slice();
    }
    /**
     * 获取实体数量
     * @returns 实体数量
     */
    count() {
        return this.entities.length;
    }
}
/**
 * 创建ECS流式API实例
 * @param scene 场景
 * @param querySystem 查询系统
 * @param eventSystem 事件系统
 * @returns ECS流式API实例
 */
export function createECSAPI(scene, querySystem, eventSystem) {
    return new ECSFluentAPI(scene, querySystem, eventSystem);
}
/**
 * 全局ECS流式API实例（需要在使用前初始化）
 */
export let ECS;
/**
 * 初始化全局ECS API
 * @param scene 场景
 * @param querySystem 查询系统
 * @param eventSystem 事件系统
 */
export function initializeECS(scene, querySystem, eventSystem) {
    ECS = createECSAPI(scene, querySystem, eventSystem);
}
//# sourceMappingURL=FluentAPI.js.map